
/**
 * @fileoverview Componente molecule TemplateSelector para seleccionar plantillas con búsqueda asíncrona y filtros avanzados.
 * Permite buscar, filtrar y seleccionar plantillas de mensajes, mostrando información relevante y optimizando performance.
 * Optimizado con React.memo, useMemo y useCallback para performance y UX.
 *
 * @module TemplateSelector
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa, optimización de memoización y callbacks.
 */
import React, { useCallback, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil, omitBy } from 'lodash';
import DropdownAsyncMC from '../../../components/DropdownAsyncMC';
import WsTemplateModelMC from '../../../models/WsTemplateModelMC';
import { templateIcon } from '../../../../util/templateIconType';
import IconMC from '../../../components/IconMC';

/**
 * Componente TemplateSelector para selección de plantillas con búsqueda asíncrona y filtros. 
 */
const TemplateSelector = memo(({
  name,
  label,
  currentCustomer,
  selectedTeam = 'all',
  selectedTemplateType = 'all',
  placeholder,
  debounceTime = 300,
  platformRetailerId,
  requirePlatformRetailerId = false,
  onSelect
}) => {
  const { t } = useTranslation('NewChat');

  const customerHasPhone = useMemo(() => Boolean(
    currentCustomer?.phone || currentCustomer?.phone_with_country_code
  ), [currentCustomer]);

  const templateRequiresPhone = useCallback((template) => (
    template.category?.toUpperCase() === 'AUTHENTICATION'
  ), []);

  // Memoizar placeholder para evitar re-renders innecesarios
  const memoizedPlaceholder = useMemo(() =>
    placeholder || t('apiMessage.selectTemplate'),
    [placeholder, t]
  );

  // Callback para formatear el texto de plantilla con variables, optimizado con useCallback
  const formatTextWithVariables = useCallback((text = '') => {
    if (!text) return '';

    const newArray = text.split('');
    let newText = '';
    let counter = 1;

    newArray.forEach((key, index) => {
      if (key === '*') {
        if (index === 0 || newArray[index - 1] !== '\\') {
          newText += `{{${counter}}}`;
          counter += 1;
        } else {
          newText += key;
        }
      } else if (key === '\\') {
        if (index === (newArray.length - 1) || newArray[index + 1] !== '*') {
          newText += key;
        } else {
          newText += '';
        }
      } else {
        newText += key;
      }
    });

    return newText;
  }, []);

  // Callback para cargar opciones de plantilla de forma asíncrona con filtros, optimizado con useCallback
  const loadTemplateOptions = useCallback(async ({ text = '' }) => {
    const formatSearchParams = (searchText = '', newPage = 1) => {
      const searchTerm = searchText.trim();
      const filters = {};

      if ((selectedTemplateType && selectedTemplateType !== 'all') || selectedTemplateType === 0) {
        filters['q[template_type_eq]'] = selectedTemplateType;
      }

      if (selectedTeam && selectedTeam !== 'all') {
        filters['q[team_id_eq]'] = selectedTeam;
      }

      if (searchTerm) {
        filters['q[text_or_name_cont]'] = searchTerm.toLowerCase();
      }

      if (requirePlatformRetailerId) {
        filters['q[platform_retailer_id_eq]'] = platformRetailerId || '__missing_platform_retailer__';
      }

      return omitBy({
        page: newPage,
        'q[s]': 'created_at DESC',
        'q[status_eq]': '1',
        ...filters
      }, isNil);
    };

    try {
      const searchParams = formatSearchParams(text);
      const response = await WsTemplateModelMC.get(searchParams);
      const wsTemplates = response?.ws_templates || [];

      const formattedOptions = wsTemplates.map((template) => {
        const requiresPhone = templateRequiresPhone(template);
        const disabled = requiresPhone && !customerHasPhone;

        return {
          id: template.id,
          label: template.name || t('apiMessage.unknownTemplate'),
          name: template.name || t('apiMessage.unknownTemplate'),
          category: template.category,
          text: formatTextWithVariables(template.text || ''),
          templateType: template.template_type,
          gupshupTemplateId: template.gupshup_template_id,
          platformRetailerId: template.platform_retailer_id,
          integrationPlatform: template.integration_platform,
          disabled,
          disabledReason: disabled ? t('apiMessage.templateRequiresPhone') : null,
          cards: template.cards?.map((card, index) => ({
            id: index,
            body: formatTextWithVariables(card.body || ''),
            headerType: card.headerType,
            buttons: card.buttons?.map((button) => ({
              text: button.text || '',
              url: formatTextWithVariables(button.url || '')
            })) || []
          })) || []
        };
      });

      return { data: formattedOptions };
    } catch (error) {
      console.error('Error loading template options:', error);
      return { data: [] };
    }
  }, [
    selectedTeam,
    selectedTemplateType,
    customerHasPhone,
    templateRequiresPhone,
    formatTextWithVariables,
    platformRetailerId,
    requirePlatformRetailerId,
    t
  ]);

  // Callback para calcular texto de plantilla principal con valores actuales, optimizado con useCallback
  const renderTemplateOption = useCallback((option) => {
    if (!option) {
      return (
        <span className="tw-text-gray-500 tw-text-sm">
          {memoizedPlaceholder}
        </span>
      );
    }

    return (
      <div className="tw-flex tw-items-start tw-w-full tw-text-left">
        <div className="tw-flex-1 tw-min-w-0 tw-pr-3">
          <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-1 tw-truncate">
            {option.name || t('apiMessage.noName')}
          </p>
          <p className="tw-text-xs tw-text-gray-600 tw-break-words tw-line-clamp-2">
            {option.text || t('apiMessage.noContent')}
          </p>
          {option.disabledReason && (
            <p className="tw-text-xs tw-text-red-500 tw-mb-0 tw-mt-1">
              {option.disabledReason}
            </p>
          )}
        </div>
        <div className="tw-w-8 tw-flex tw-justify-center tw-flex-shrink-0">
          {option.templateType !== 'carousel' && (
            <i
              className={`${templateIcon(option.templateType)} tw-text-lg tw-text-gray-500`}
              aria-hidden="true"
            />
          )}
          {option.templateType === 'carousel' && (
            <IconMC name="carousel-outline" size="sm" className="tw-text-gray-500" />
          )}
        </div>
      </div>
    );
  }, [memoizedPlaceholder, t]);

  // Memoizar las props del DropdownAsyncMC para evitar re-renders innecesarios
  const dropdownProps = useMemo(() => ({
    name,
    label,
    loadOptions: loadTemplateOptions,
    renderOption: renderTemplateOption,
    renderSelected: renderTemplateOption,
    useFloatingLabel: true,
    placeholder: memoizedPlaceholder,
    debounceTime,
    ...(onSelect && { onSelect })
  }), [name, label, loadTemplateOptions, renderTemplateOption, memoizedPlaceholder, debounceTime, onSelect]);


  /**
   * Renderiza el selector de plantillas con búsqueda asíncrona y filtros.
   */
  return (
    <div className="tw-wrapper">
      <DropdownAsyncMC {...dropdownProps} />
    </div>
  );
});


/**
 * Nombre de display para el componente TemplateSelector.
 */
TemplateSelector.displayName = 'TemplateSelector';

export default TemplateSelector;
